import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, MessageSquare, Users, BookOpen, HelpCircle, Megaphone, UserPlus, Store, PhoneOff, Clock } from "lucide-react";
import { useHasRole } from "@/hooks/useUserRoles";
import { useEmailPreferences, useUpdateEmailPreferences } from "@/hooks/useEmailPreferences";

interface PreferenceItemProps {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

function PreferenceItem({ id, label, description, icon, checked, onCheckedChange, disabled }: PreferenceItemProps) {
  return (
    <div className="flex items-center justify-between py-4 border-b last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-muted-foreground">{icon}</div>
        <div className="space-y-0.5">
          <Label htmlFor={id} className="text-base font-medium cursor-pointer">
            {label}
          </Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

export function EmailPreferencesSection() {
  const { data: preferences, isLoading } = useEmailPreferences();
  const updatePreferences = useUpdateEmailPreferences();
  const isAdmin = useHasRole("admin");

  const handleToggle = (key: string, value: boolean) => {
    updatePreferences.mutate({ [key]: value });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-center justify-between py-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return null;
  }

  const isPending = updatePreferences.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Email Notifications
        </CardTitle>
        <CardDescription>
          Choose which email notifications you'd like to receive
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PreferenceItem
          id="notify_announcements"
          label="Announcements"
          description="Get notified about new platform announcements and updates"
          icon={<Megaphone className="h-5 w-5" />}
          checked={preferences.notify_announcements}
          onCheckedChange={(checked) => handleToggle("notify_announcements", checked)}
          disabled={isPending}
        />
        <PreferenceItem
          id="notify_new_courses"
          label="New Courses & Curricula"
          description="Get notified when new courses or curricula are available"
          icon={<BookOpen className="h-5 w-5" />}
          checked={preferences.notify_new_courses}
          onCheckedChange={(checked) => handleToggle("notify_new_courses", checked)}
          disabled={isPending}
        />
        <PreferenceItem
          id="notify_discussion_replies"
          label="Discussion Replies"
          description="Get notified when someone replies to discussions you've participated in"
          icon={<MessageSquare className="h-5 w-5" />}
          checked={preferences.notify_discussion_replies}
          onCheckedChange={(checked) => handleToggle("notify_discussion_replies", checked)}
          disabled={isPending}
        />
        <PreferenceItem
          id="notify_connection_requests"
          label="Connection Requests"
          description="Get notified about new connection requests and updates"
          icon={<Users className="h-5 w-5" />}
          checked={preferences.notify_connection_requests}
          onCheckedChange={(checked) => handleToggle("notify_connection_requests", checked)}
          disabled={isPending}
        />
        <PreferenceItem
          id="notify_help_requests"
          label="Help Requests"
          description="Get notified about new help requests from learners"
          icon={<HelpCircle className="h-5 w-5" />}
          checked={preferences.notify_help_requests}
          onCheckedChange={(checked) => handleToggle("notify_help_requests", checked)}
          disabled={isPending}
        />
        <PreferenceItem
          id="notify_messages"
          label="Direct Messages"
          description="Get notified when you receive a new message"
          icon={<MessageSquare className="h-5 w-5" />}
          checked={preferences.notify_messages}
          onCheckedChange={(checked) => handleToggle("notify_messages", checked)}
          disabled={isPending}
        />
        {isAdmin && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Admin notifications</h3>
            {([
              ["notify_admin_new_signups", "New member signups", "Email me when someone creates an EAB Connect account and their profile needs approval.", <UserPlus className="h-5 w-5" />],
              ["notify_admin_topshop_applications", "New Top Shop applications", "Email me when someone applies to the Top Shop Challenge.", <Store className="h-5 w-5" />],
              ["notify_admin_topshop_submissions", "Top Shop challenge submissions", "Email me when a Top Shop participant marks a challenge ready for scoring.", <Store className="h-5 w-5" />],
              ["notify_admin_call_cancellations", "Call cancellations", "Email me a copy when a member cancels a scheduled call.", <PhoneOff className="h-5 w-5" />],
              ["notify_admin_stale_connections", "Unanswered connection requests", "Email me when a connection request has had no reply for 72 hours.", <Clock className="h-5 w-5" />],
            ] as const).map(([key, label, description, icon]) => (
              <PreferenceItem
                key={key}
                id={key}
                label={label}
                description={description}
                icon={icon}
                checked={preferences[key]}
                onCheckedChange={(checked) => handleToggle(key, checked)}
                disabled={isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}